/**
 * MixpanelInit — initialises Mixpanel once on app load.
 *
 * Capabilities:
 * - User identification: HMAC-SHA256 hash of email (keyed with
 *   `frontend.mixpanelIdentityHmacSecret`) as distinct_id — never the raw
 *   email, and never an unkeyed digest either (see hashIdentity.ts), first_seen
 * - Super properties: app_version, environment, user_team (from first group ownership ref) attached to every event
 * - AnalyticsApi integration: page_viewed, page_exited, search events via MixpanelAnalyticsApi
 * - Profile properties from the catalog User entity (`user/role`,
 *   `user/organization`, `user/department` → job_title, job_title_normalized,
 *   job_group, organization, department); sent after identify and never
 *   blocks markAnalyticsReady
 *
 * Environment is derived from app.baseUrl:
 *   portfolio.arrive.com → "production"
 *   anything else        → "staging"
 *
 * On completion, calls markAnalyticsReady() to flush any events that were
 * buffered by MixpanelAnalyticsApi before init/identify finished.
 */
import { useEffect } from 'react';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import mixpanel from 'mixpanel-browser';
import { markAnalyticsReady } from '../lib/analytics/MixpanelAnalyticsApi';
import { getPageTitle } from '../lib/analytics/pageTitle';
import { hashEmail } from '../lib/analytics/hashIdentity';
import { beforeTrackHook } from '../lib/analytics/session';
import { buildJobProfileProperties } from '../lib/analytics/jobProfile';

/**
 * Reads the User entity and writes job profile properties to Mixpanel.
 * Never throws or rejects: every failure is swallowed so analytics can't
 * affect the app. Called without await.
 */
async function sendJobProfile(
  catalogApi: Pick<typeof catalogApiRef.T, 'getEntityByRef'>,
  userEntityRef: string | undefined,
): Promise<void> {
  try {
    if (!userEntityRef) {
      return;
    }
    const entity = await catalogApi.getEntityByRef(userEntityRef);
    const properties = buildJobProfileProperties(entity);
    if (Object.keys(properties).length > 0) {
      mixpanel.people.set(properties);
    }
  } catch (_e) {
    // catalog or mixpanel failed — the profile updates on the next sign-in.
  }
}

// Once-per-page-load guard. Module scope (not the SDK's internal `__loaded`)
// so it survives wrapper remounts and does not depend on mixpanel-browser.
let initStarted = false;

/** Test-only: lets each test start from a fresh page-load state. */
export function resetMixpanelInitForTests() {
  initStarted = false;
}

export function MixpanelInit() {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  useEffect(() => {
    // Wrap the entire init flow defensively. Mixpanel can throw if it is
    // blocked (ad blocker), localStorage is unavailable, or the SDK fails
    // for any other reason. We must never let an analytics failure surface
    // as an unhandled exception inside React. In every failure path we
    // still call markAnalyticsReady() so buffered events drain (they will
    // fail silently downstream) and the buffer cannot grow.
    const frontendConfig = config.getOptionalConfig('frontend');
    const projectToken = frontendConfig?.getOptionalString(
      'mixpanelProjectToken',
    );
    if (!projectToken) {
      markAnalyticsReady();
      return;
    }

    // The app root wrapper can mount this twice per page load (bootstrap and
    // finalized trees). Re-running init would re-register hooks and reset
    // state, so later mounts leave the first one in charge. They must not call
    // markAnalyticsReady(): the first mount's identify chain owns that, so
    // buffered events are never flushed under the anonymous id.
    if (initStarted) {
      return;
    }
    initStarted = true;

    // Set document.title for the initial page load so that Mixpanel's
    // built-in $mp_web_page_view fires with a meaningful title rather
    // than the generic HTML title or an empty string.
    // Guarded separately so a missing document (e.g. SSR/test env) cannot
    // prevent mixpanel.init from running.
    try {
      document.title = getPageTitle(window.location.pathname);
    } catch (_e) {
      // silent
    }

    try {
      mixpanel.init(projectToken, {
        // track_pageview is deliberately DISABLED. When enabled, mixpanel
        // fires the first $mp_web_page_view synchronously inside init() —
        // before identify() has run (identity resolves async, below) — so
        // that pageview and the Session Start derived from it get stamped
        // with the anonymous $device: id, producing "undefined" user
        // profiles. Instead, MixpanelAnalyticsApi fires $mp_web_page_view
        // itself on every navigation through its post-identify buffer, so
        // the built-in DAU metric, web analytics report, and page
        // performance graph still work but always land on the identified
        // user. See MixpanelAnalyticsApi.handleNavigate.
        track_pageview: false,
        persistence: 'localStorage',
        api_host: 'https://api-eu.mixpanel.com',
        // Stamps session_id on direct mixpanel.track calls that bypass
        // MixpanelAnalyticsApi. Must always return an array, or the SDK drops
        // the event.
        hooks: { before_track: beforeTrackHook },
      });

      const baseUrl = config.getOptionalString('app.baseUrl') ?? '';
      const environment =
        frontendConfig?.getOptionalString('mixpanelEnvironment') ??
        (baseUrl.includes('portfolio.arrive.com') ? 'production' : 'staging');
      const appVersion = config.getOptionalString('app.version') ?? 'unknown';

      mixpanel.register({
        app_version: appVersion,
        environment,
      });
    } catch (_e) {
      markAnalyticsReady();
      return;
    }

    Promise.all([
      identityApi.getProfileInfo(),
      identityApi.getBackstageIdentity(),
    ])
      .then(async ([profile, identity]) => {
        try {
          const email = profile.email ?? '';

          const groupRef = identity.ownershipEntityRefs?.find(ref =>
            ref.startsWith('group:'),
          );
          const userTeam = groupRef?.split('/').pop() ?? '';
          if (userTeam) {
            mixpanel.register({ user_team: userTeam });
          }

          // Required for keying the identify hash (see hashIdentity.ts).
          // Deliberately not falling back to an unsalted hash when this is
          // missing — that would silently regress to the reversible
          // digest this config exists to prevent, so we skip identify
          // entirely instead (same as the missing-email/no-crypto paths).
          const identityHmacSecret = frontendConfig?.getOptionalString(
            'mixpanelIdentityHmacSecret',
          );

          if (email && identityHmacSecret) {
            const hashedId = await hashEmail(email, identityHmacSecret);
            mixpanel.identify(hashedId);
            // Per data-model.md, the user profile uses `team` (not
            // `user_team`, which is the super property name).
            // $name/$email are never sent — see hashIdentity.ts and PAT-915.
            mixpanel.people.set({
              // Only set `team` when a group ref was resolved; otherwise
              // we would clobber an existing profile value with an empty
              // string.
              ...(userTeam ? { team: userTeam } : {}),
            });
            mixpanel.people.set_once({
              first_seen: new Date().toISOString(),
            });
            void sendJobProfile(catalogApi, identity.userEntityRef);
          }
        } catch (_e) {
          // identify/people calls failed — events still flush.
        }
      })
      .catch(() => {
        // Identity unavailable — events still flush with an anonymous ID.
      })
      .finally(() => {
        markAnalyticsReady();
      });
  }, [config, identityApi, catalogApi]);

  return null;
}
